import { useSuspenseQuery, type UseSuspenseQueryResult } from "@apollo/client/react";
import {
  DreamSearchForDreamSearchQuery,
  DreamSearchForDreamSearchDocument,
  DreamSearchForDreamSearchQueryVariables,
} from "./generated";
import { createGenericContext } from "@portal/app/_common/lib/createGenericContext";
import type { ErrorLike } from "@apollo/client/core";
import { getCityStateAndZip } from "./utils/searchHelpers";
import { Suspense, useEffect, useMemo, useState } from "react";
import { LandingPageBySlug } from "../../view/[slug]/SEOMetroPage";
import { gql } from "graphql-tag";
import { LoadingIndicator } from "@portal/app/_components/ui-elements/LoadingIndicator";
import { Filters } from "@portal/app/_common/types/dreamSearch";
import { ChildrenFunction } from "@portal/app/_common/types/utils";
import { redirect } from "next/navigation";
import { getUrls } from "@portal/app/_common/lib/urls";
import { isDegradableGeocodeError } from "@portal/app/_common/lib/clientErrorUtils";
import { FALLBACK_ZIP } from "@portal/app/_common/lib/requestUtils";

/**
 * Fields that you fetch here must be defined in `interpretDreamSearchFilters`
 * because the Program that's added to the DataLoader does not include all
 * fields (for performance reasons).
 */
void gql`
  fragment DreamSearchItemFragment on DreamSearchItem {
    id
    score
    distance
    favorited
    label
    provider {
      id
      fullName
      logoImageFile {
        id
        url
      }
      isPartnered
      slug
      publicDisplayFields {
        id
        aboutSchool
      }
      geocode {
        id
        point
      }
      detailsMap {
        id
        logoPalette {
          muted
        }
      }
      complianceExclusionTags
    }
    program {
      id
      environment
      environmentCompliance
      name
      slug
      customCtaCopy
      preLeadRedirect
      preLeadRedirectParams
      preLeadRedirectCompositeParams
      preLeadRedirectSuppressDefaultParams
      preLeadRedirectInIframe
      allowHybridSignup
      publicDisplayFields {
        id
        aboutProgramPlainText
      }
      programHighlights
      vocation {
        id
        abbreviation
        adjective
      }
      costs {
        id
        cost
      }
      eligiblePaymentOptions: loaderEligiblePaymentOptions {
        id
        type
        estimatedFirstPayment
        config {
          id
          description
        }
        isVisible
      }
      tags
      programLevel
    }
  }
  query dreamSearchForDreamSearch(
    $dreamSearchFilters: DreamSearchFilters!
    $geo: DreamSearchGeo!
    $first: Int!
    $after: Int
    $complianceExclusionTags: [String!]!
    $pinnedProgramId: ID
  ) {
    dreamSearchItemsConnection(
      filter: $dreamSearchFilters
      geo: $geo
      first: $first
      after: $after
      complianceExclusionTags: $complianceExclusionTags
      pinnedProgramId: $pinnedProgramId
      includeFilterSettings: true
      includeOtherRecommendedItems: true
    ) {
      edges {
        cursor
        node {
          ...DreamSearchItemFragment
        }
      }
      city
      state
      zip
      filterSettings {
        vocation {
          id
          abbreviation
          adjective
        }
        environment
        paymentOptionType
        programDuration
        programLevel
        programTag
        subindustries
        industries
      }
      otherRecommendedItems {
        ...DreamSearchItemFragment
      }
      totalCount
      pageInfo {
        hasNextPage
      }
    }
  }
`;

type FilterSettings =
  DreamSearchForDreamSearchQuery["dreamSearchItemsConnection"]["filterSettings"];
type Items =
  DreamSearchForDreamSearchQuery["dreamSearchItemsConnection"]["edges"][number]["node"][];
type OtherRecommendedItems =
  DreamSearchForDreamSearchQuery["dreamSearchItemsConnection"]["otherRecommendedItems"];

export type DreamSearchContextProps = {
  /**
   * Connection fields
   */
  items: Items | undefined;
  filterSettings: FilterSettings | undefined;
  otherRecommendedItems: OtherRecommendedItems | undefined;
  totalCount: number | undefined;
  hasNextPage: boolean;
  isLoading: boolean;
  /**
   * Additional helpers
   */
  city: string;
  state: string;
  zip: string;
  industry?: GQL.VocationIndustry;
  vocation?: {
    id: string;
    industry: string;
    name: string;
  } | null;
  error: ErrorLike | undefined;
  fetchMore: UseSuspenseQueryResult<
    DreamSearchForDreamSearchQuery,
    DreamSearchForDreamSearchQueryVariables
  >["fetchMore"];
};

const [useDreamSearchContext, DreamSearchContext] =
  createGenericContext<DreamSearchContextProps>("DreamSearchContext");

export { useDreamSearchContext };

export const DreamSearchProvider = ({
  variables,
  industry,
  vocation,
  landingPage,
  filters,
  setFilters,
  children,
}: {
  variables: {
    dreamSearchFilters: GQL.DreamSearchFilters;
    geo: { geoCenter: GQL.DreamSearchGeoCenter };
    first: number;
    complianceExclusionTags: string[];
    pinnedProgramId?: string | null;
  };
  industry?: GQL.VocationIndustry;
  vocation?: {
    id: string;
    industry: string;
    name: string;
  } | null;
  landingPage?: LandingPageBySlug;
  filters?: Filters;
  setFilters?: React.Dispatch<React.SetStateAction<Filters>>;
  children: ChildrenFunction<DreamSearchContextProps>;
}) => {
  const { data, error, fetchMore } = useSuspenseQuery(DreamSearchForDreamSearchDocument, {
    variables,
    errorPolicy: "all",
  });

  // Redirect on invalid address (e.g., non-US visitor, unrecognized ZIP) or a
  // geocode outage.
  // Uses the same fallback ZIP as getGeoCenterFromRequest (context.ts:58) to
  // prevent an infinite loop: redirecting to zip=null would re-resolve from
  // Cloudflare headers, which may return the same invalid ZIP.
  // Must be in render (not useEffect) because redirect() throws NEXT_REDIRECT.
  if (isDegradableGeocodeError(error)) {
    redirect(getUrls().DreamSearch({ industry, zip: FALLBACK_ZIP, shouldPromptForLocation: true }));
  }

  const connection = data?.dreamSearchItemsConnection;
  /**
   * Putting this in state to avoid re-rendering of the whole filter section
   * every time the search is run
   */
  const [filterSettings, setFilterSettings] = useState<FilterSettings | undefined>(
    connection?.filterSettings
  );

  const { city, state, zip } = useMemo(
    () =>
      getCityStateAndZip(
        connection?.city, // Splitting this out to avoid re-rendering on the whole connection
        connection?.state,
        connection?.zip,
        variables.geo.geoCenter,
        landingPage
      ),
    [connection?.city, connection?.state, connection?.zip, landingPage, variables.geo.geoCenter]
  );

  const context = useMemo(
    () =>
      ({
        items: connection?.edges.map(({ node }) => node),
        filterSettings,
        otherRecommendedItems: connection?.otherRecommendedItems,
        totalCount: connection?.totalCount,
        hasNextPage: connection?.pageInfo?.hasNextPage ?? false,
        isLoading: !connection?.edges,
        error,
        city,
        state,
        zip,
        vocation,
        industry,
        fetchMore,
      }) satisfies DreamSearchContextProps,
    [
      city,
      error,
      filterSettings,
      industry,
      state,
      vocation,
      zip,
      fetchMore,
      connection?.otherRecommendedItems,
      connection?.totalCount,
      connection?.pageInfo?.hasNextPage,
      connection?.edges,
    ]
  );

  useEffect(() => {
    if (connection?.filterSettings) {
      setFilterSettings(connection.filterSettings);
    }
  }, [connection?.filterSettings]);

  useEffect(() => {
    /**
     * Sometimes we have external links to dream search that have a vocation
     * that's been deleted. In that case, the filters will have the deleted
     * vocation in the program_vocation_in filter. This will remove the deleted
     * vocation from the filter.
     */
    if (
      filters?.program_vocation_in?.filter(
        (vocationId) => !filterSettings?.vocation.find(({ id }) => id === vocationId)
      ).length &&
      setFilters
    ) {
      setFilters({
        ...filters,
        program_vocation_in: filters.program_vocation_in.filter((vocationId) =>
          filterSettings?.vocation.find(({ id }) => id === vocationId)
        ),
      });
    }
  }, [filterSettings?.vocation, filters, setFilters]);

  return (
    <Suspense fallback={<LoadingIndicator fullScreen />}>
      <DreamSearchContext.Provider value={context}>{children(context)}</DreamSearchContext.Provider>
    </Suspense>
  );
};
